import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, GraduationCap, Building2, CreditCard, Sparkles, Home } from 'lucide-react';
import { Button } from '../ui/button';

const MainNavigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { 
      label: 'Home', 
      path: '/',
      icon: Home,
      gradient: 'from-pink-500 to-rose-500'
    },
    { 
      label: 'For Trainers', 
      path: '/for-trainers',
      icon: GraduationCap,
      gradient: 'from-blue-500 to-purple-500'
    },
    { 
      label: 'For Organizations', 
      path: '/for-organizations',
      icon: Building2,
      gradient: 'from-indigo-500 to-purple-500'
    },
    { 
      label: 'Pricing', 
      path: '/pricing',
      icon: CreditCard,
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center space-x-3 group"
          >
            <div className="relative w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-lg font-black text-gray-900 leading-tight">TrainerLinked</span>
              <span className="text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">.in</span>
            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`
                    relative px-6 py-3 rounded-lg font-semibold transition-all duration-200
                    ${active 
                      ? 'text-white' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                >
                  {active && (
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} rounded-lg shadow-lg`}></div>
                  )}
                  <div className="relative flex items-center gap-4">
                    <Icon className="w-6 h-6" />
                    <span className="text-lg">{item.label}</span>
                  </div>
                </button>
              );
            })}
            
            <Button 
              onClick={() => navigate('/login')}
              className="ml-8 px-8 py-3 text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t bg-white py-4">
            <div className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-5 px-6 py-4 rounded-lg transition-all
                      ${active 
                        ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg` 
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="w-7 h-7" />
                    <span className="font-semibold text-lg">{item.label}</span>
                  </button>
                );
              })}
              
              <Button 
                onClick={() => {
                  navigate('/login');
                  setIsMenuOpen(false);
                }}
                className="w-full mt-4 py-4 text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default MainNavigation;
